import React, { useState, useEffect, useRef, useReducer} from 'react';
import axios from 'axios';
import moment from 'moment';
import { useLocalStorage, useDebounce } from 'react-use';
import { SpreadsheetComponent } from '@syncfusion/ej2-react-spreadsheet';
import {
  Button,
  Card,
  Col,
  Form,
  Row,
} from 'react-bootstrap';
import FileUpload from 'components/FileUpload/FileUpload.js';
import useNotif from 'hooks/use-notify-hook';
import PlanContext from './Utils/context.js';
import PlanReducer from './Utils/reducer.js';
import { ACTIONS } from './Utils/action.js';
import AnswerAction from './AnswerAction';
import AnswerRepository from 'repository/Answer.js';

export default function Table({ answer, scores, auditeeTeams = [], scheduleId, actionTypes = []}) {

  let spreadsheet = useRef(null);

  const [auth] = useLocalStorage('authenticated');
  const [score, setScore] = useState(scores.find(item => item.INDEX_ID === answer.INDEX_ID)?.INDEX_ID ?? `n/a`);
  const [notif, dispatchNotif] = useNotif();
  const [data, setData] = useState(answer.ANSWER_DESC);
  const [editEv, setEditEv] = useState(false);
  const [evaluate, setEvaluate] = useState(answer?.EVALUATE_DESC ?? null);
  const [attachments, setAttachments] = useState(answer?.answerAttachments ?? []);
  const [cause, setCause] = useState(answer?.CAUSE_DESC ?? ``);
  const [isReadonly, setIsReadonly] = useState(true);
  const [actions, dispatchActions] = useReducer(PlanReducer, answer?.answerActions ?? []);

  const repositories = new AnswerRepository(auth.token);

  let filePromises = [];

  const handleUpload = async (file) => {

    try {
      filePromises.push(repository.upload(answer.ANSWER_ID, file));
      const promises = await Promise.all(filePromises);
      // setAttachments([...attachments, ...promises.map(item => item.data)]);
      promises.map(item => attachments.push(item.data));
      dispatchNotif({
        place: `br`,
        message: `Upload Success...`,
        type: `success`,
      });

      filePromises = [];

    } catch(ex) {

      dispatchNotif({
        place: `br`,
        message: `Upload Failed ${ex.toString()}`,
        type: `danger`,
      });

    }
  }

  const handleChangeScore = async ({target: {value}}) => {

    try {

      const response = await axios.put(
        `${process.env.REACT_APP_API_HOST}answers/${answer.ANSWER_ID}`,
        {INDEX_ID: value},
        {headers: {Authorization: `Bearer ${auth.token}`}},
      );

      dispatchNotif({
        place: `br`,
        message: `Update Success...`,
        type: `success`,
      });

      setScore(value);

    } catch (error) {

      console.log(error);
      alert(error);
    }
  }

  const handleAddAction = async () => {

    try {

      const response = await axios.post(
        `${process.env.REACT_APP_API_HOST}answer-actions`,
        {
          ACTION_TYPE_ID: 1,
          ANSWER_ID: answer.ANSWER_ID,
        },
        {headers: {Authorization: `Bearer ${auth.token}`}}
      );

      dispatchActions({
        type: ACTIONS.CREATE,
        payload: {
          ANSWER_ACTION_ID: response.data.ANSWER_ACTION_ID,
          ACTION_TYPE_ID: response.data.ACTION_TYPE_ID,
          ANSWER_ID: response.data.ANSWER_ID,
        }
      });

    } catch (error) {
      dispatchNotif({
        place: `br`,
        message: `Failed: ${error.toString()}`,
        type: `danger`,
      });
    }
  }

  const handleCreated = async () => {

    if (data === null || data === undefined) return;
    if (!spreadsheet.current) return;

    const response = await axios.get(
      `${process.env.REACT_APP_API_HOST}answers/${answer.ANSWER_ID}/spreadsheet`,
    );
    console.log(response.data)
    if(response.data){
      spreadsheet.current.openFromJson({file: response.data});
    }
  }

  const handleUpdate = async () => {
    const response = await axios.put(
      `${process.env.REACT_APP_API_HOST}answers/${answer.ANSWER_ID}`,
      {
        CAUSE_AT: moment().format('DD-MM-YYYY hh:mm:ss'),
        CAUSE_BY: auth.employee.EMP_ID,
        CAUSE_DESC: cause,
      },
      {headers: {Authorization: `Bearer ${auth.token}`}}
    );
  }

  const [, cancelCause] = useDebounce(async () => {

    handleUpdate();

    dispatchNotif({
      place: `br`,
      message: `Update Success...`,
      type: `success`,
    });

  }, 2000, [cause]);

  const [, cancelEvaluation] = useDebounce(async () => {

    const data = {
      EVALUATE_AT: moment().format('DD-MM-YYYY hh:mm:ss'),
      EVALUATE_BY: auth.employee.EMP_ID,
      EVALUATE_DESC: evaluate,
    };

    const response = await repositories.answer(answer.ANSWER_ID, data);

    dispatchNotif({
      place: `br`,
      message: `Update Success...`,
      type: `success`,
    });

  }, 2000, [evaluate]);

  const handleSaveTable = async () => {

    try {

      if (!spreadsheet.current) return;

      spreadsheet.current.endEdit();
      let json = await spreadsheet.current.saveAsJson()
      const response = await axios.put(
        `${process.env.REACT_APP_API_HOST}answers/${answer.ANSWER_ID}/spreadsheet`,
        {ANSWER_DESC: JSON.stringify(json.jsonObject)},
        {headers: {Authorization: `Bearer ${auth.token}`}},
      );

      dispatchNotif({
        place: `br`,
        message: `Update Success...`,
        type: `success`,
      });

    } catch (error) {
      console.log(error)
      alert(error)
    }
  }

  useEffect(() => {
    cancelCause();
    cancelEvaluation();
  }, []);


  return (
    <PlanContext.Provider value={{
      scheduleId,
      answer,
      auditeeTeams,
      actionTypes,
      actions, dispatchActions
    }}>
      <Row>
        {notif}
        <Col md="12">
          <Card>
            <Card.Header>
              <Card.Title style={{fontWeight:'bold', fontSize:'20px', marginBottom:10}} as="h5">{answer.question.QUESTION_CODE}. <a style={{fontWeight:'bold'}} dangerouslySetInnerHTML={{__html: answer.question.QUESTION_LABEL}} /></Card.Title>
              <div style={{whiteSpace: 'pre-wrap', marginLeft:18, marginTop:5, fontSize:'17px'}} dangerouslySetInnerHTML={{__html: answer.question.QUESTION_DESC}} />
              <hr></hr>
            </Card.Header>
            <Card.Body>
              <Row>
                <Col md="8">
                  <Form.Group>
                    <Form.Label style={{fontSize:'17px'}}>Auditee's Answer</Form.Label>
                    <SpreadsheetComponent
                      ref={spreadsheet}
                      isProtected={false}
                      allowEditing={true}
                      showFormulaBar={true}
                      showSheetTabs={true}
                      created={handleCreated}
                      openUrl={'https://ej2services.syncfusion.com/production/web-services/api/spreadsheet/open'}
                      saveUrl={'https://ej2services.syncfusion.com/production/web-services/api/spreadsheet/save'}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Button variant="primary" onClick={handleSaveTable}>Save Table</Button>
                  </Form.Group>
                  <Form.Group>
                    {/* <Form.Label>Auditor's Evaluation</Form.Label>
                    <p>{answer.EVALUATE_DESC ?? 'N/A'}</p> */}
                    <Form.Label style={{fontSize:'17px'}}>Auditor's Evaluation</Form.Label>
                    <div></div>
                    {!editEv ?
                      <Button onClick={() => {setEditEv(true)}} variant="primary" size='sm'>Edit</Button>
                    :''}
                    {editEv ?
                      <div>
                        <Form.Control
                          value={evaluate}
                          onChange={({ target: { value }}) => setEvaluate(value)}
                          as="textarea"
                          rows={3}
                          isInvalid={evaluate?.length > 4000}
                        />
                        <Form.Text id="passwordHelpBlock" muted>
                          Total characters: {evaluate?.length ?? 0} / 4000
                        </Form.Text>
                      </div>
                    :<p>{evaluate ?? 'N/A'}</p>
                    }
                  </Form.Group>
                  <Form.Group>
                    <Form.Label style={{fontSize:'17px'}}>Root Cause</Form.Label>
                    <Form.Control
                      value={cause}
                      onChange={({target: {value}}) => setCause(value)}
                      as="textarea"
                      rows={3}
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label style={{fontSize:'17px'}}>Action Plan's</Form.Label>
                    {actions && actions.length > 0 ? actions.map((item, key) => <AnswerAction key={key} action={item} />) : <p>No action plan defined.</p>}
                  </Form.Group>
                  <Form.Group>
                    <Button onClick={handleAddAction} variant="primary">Add New Action</Button>
                  </Form.Group>
                </Col>
                <Col md="4">
                  <Form.Group>
                    <Form.Label style={{fontSize:'17px'}}>Score</Form.Label>
                    <Form.Control
                      as="select"
                      value={score}
                      onChange={handleChangeScore}
                    >
                      <option value={-1}>Please select</option>
                      {scores && scores.map((score, key) => (
                        <option key={key} value={score.INDEX_ID}>{score.SCORE_INDEX}</option>
                      ))}
                    </Form.Control>
                  </Form.Group>
                  <Form.Group>
                    <Form.Label style={{fontSize:'17px'}}>Attachment(s)</Form.Label>
                    {attachments && attachments.length > 0
                      ? (
                        <span className="form-control"
                          style={{
                            maxHeight: `200px`,
                            height: `200px`,
                            overflowY: `scroll`,
                            marginBottom: `10px`
                          }}
                        >
                          {attachments.map((attachment, key) => (
                            <div key={key} className="font-icon-detail">
                              <i className="nc-icon nc-attach-87"></i>{' '}
                              <a href={`${process.env.REACT_APP_API_HOST}answer-attachments/${attachment.ATTACHMENT_ID}/download`} target="_blank" rel="noopener noreferrer">
                                {attachment.ATTACHMENT_NAME}.{attachment.ATTACHMENT_EXT}
                              </a>
                            </div>
                          ))}
                        </span>
                      )
                      : <p>No attachment uploaded.</p>
                    }
                    <FileUpload
                      onFileSelectSuccess={(file) => handleUpload(file)}
                      onFileSelectError={({ error }) => alert(error)}
                    >
                      Upload
                    </FileUpload>
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </PlanContext.Provider>
  );
}